import React from 'react';
import { FormState } from '../../types';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Alert } from '../ui/Alert';
import { LookupField } from '../ui/LookupField';
import { contactDirectory } from '../../data/mockData';
interface Props {
  state: FormState;
  onChange: (field: keyof FormState, value: any) => void;
}
export function ContactsSection({
  state,
  onChange
}: Props) {
  const isPurchase = state.loanPurpose === 'Purchase';
  const requiresAttorney = ['NY', 'CT', 'TX'].includes(state.propertyState);
  const requiresHOA = ['Condo', 'Co-op', 'High-Rise Condo', 'Detached Condo'].includes(state.propertyType);
  const isPreferredContactMissing = !state.preferredContactLanguage || !state.preferredContactMethod;
  return <div className="space-y-10" data-id="element-129">
      {/* Borrower */}
      <section data-id="element-130">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-4 uppercase tracking-wider" data-id="element-131">
          Borrower
        </h3>
        {isPreferredContactMissing && <Alert variant="warning" title="Missing Information" className="mb-6" data-id="element-132">
            Preferred contact information is incomplete. Complete these fields
            before submitting disclosures.
          </Alert>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4" data-id="element-133">
          <div className="lg:col-span-2" data-id="element-134">
            <Input label="Borrower First Name" value={state.borrowerFirstName} onChange={e => onChange('borrowerFirstName', e.target.value)} required data-id="element-135" />
          </div>
          <Input label="Borrower Middle Name" value={state.borrowerMiddleName} onChange={e => onChange('borrowerMiddleName', e.target.value)} data-id="element-136" />
          <Input label="Borrower Last Name" value={state.borrowerLastName} onChange={e => onChange('borrowerLastName', e.target.value)} required data-id="element-137" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-138">
          <div className="lg:col-span-2" data-id="element-139">
            <Input label="Borrower Email" type="email" value={state.borrowerEmail} onChange={e => onChange('borrowerEmail', e.target.value)} required data-id="element-140" />
          </div>
          <Input label="Borrower Mobile" type="tel" value={state.borrowerMobile} onChange={e => onChange('borrowerMobile', e.target.value)} required data-id="element-141" />
          <Select label="Preferred Contact Language" value={state.preferredContactLanguage} onChange={e => onChange('preferredContactLanguage', e.target.value)} options={[{
          label: 'English',
          value: 'English'
        }, {
          label: 'Spanish',
          value: 'Spanish'
        }]} required data-id="element-142" />
          <Select label="Preferred Contact Method" value={state.preferredContactMethod} onChange={e => onChange('preferredContactMethod', e.target.value)} options={[{
          label: 'Email',
          value: 'Email'
        }, {
          label: 'Phone',
          value: 'Phone'
        }, {
          label: 'Text',
          value: 'Text'
        }]} required data-id="element-143" />
        </div>
      </section>

      {/* General Contacts */}
      <section className="pt-8 border-t border-slate-100 dark:border-slate-700" data-id="element-144">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-4 uppercase tracking-wider" data-id="element-145">
          File Contacts
        </h3>
        <p className="mb-4 -mt-2 text-sm text-slate-500" data-id="element-146">
          Search and select existing contacts from the database.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-147">
          <LookupField label="Referred By" value={state.referredBy} onChange={v => onChange('referredBy', v)} options={contactDirectory.filter(c => c.category === 'Referral')} placeholder="Search referral sources…" data-id="element-148" />
          <LookupField label="Title Company Name" value={state.titleCompanyName} onChange={v => onChange('titleCompanyName', v)} options={contactDirectory.filter(c => c.category === 'Title')} placeholder="Search title companies…" data-id="element-149" />
          <LookupField label="Settlement Agent" value={state.settlementAgent} onChange={v => onChange('settlementAgent', v)} options={contactDirectory.filter(c => c.category === 'Settlement')} placeholder="Search settlement agents…" data-id="element-150" />
          <LookupField label="HOI Provider" value={state.hoiProvider} onChange={v => onChange('hoiProvider', v)} options={contactDirectory.filter(c => c.category === 'HOI')} placeholder="Search HOI providers…" data-id="element-151" />
        </div>
      </section>

      {/* Real Estate Agents (Purchase Only) */}
      {isPurchase && <section className="pt-8 border-t border-slate-100 dark:border-slate-700" data-id="element-152">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-4 uppercase tracking-wider" data-id="element-153">
            Real Estate Agents
          </h3>
          {(!state.buyerAgent || !state.listingAgent) && <Alert variant="warning" className="mb-6" data-id="element-154">
              Buyer's Agent and Listing Agent are required for purchase loans.
            </Alert>}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-155">
            <LookupField label="Buyer's Agent" value={state.buyerAgent} onChange={v => onChange('buyerAgent', v)} options={contactDirectory.filter(c => c.category === 'Agent')} placeholder="Search agents…" required data-id="element-156" />
            <LookupField label="Listing Agent" value={state.listingAgent} onChange={v => onChange('listingAgent', v)} options={contactDirectory.filter(c => c.category === 'Agent')} placeholder="Search agents…" required data-id="element-157" />
          </div>
        </section>}

      {/* Attorneys (State Specific) */}
      {isPurchase && requiresAttorney && <section className="pt-8 border-t border-slate-100 dark:border-slate-700" data-id="element-158">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-4 uppercase tracking-wider" data-id="element-159">
            Attorneys
          </h3>
          <Alert variant="info" className="mb-6" data-id="element-160">
            Attorney information is required for purchase loans in{' '}
            {state.propertyState}.
          </Alert>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-161">
            <LookupField label="Buyer's Attorney" value={state.buyerAttorney} onChange={v => onChange('buyerAttorney', v)} options={contactDirectory.filter(c => c.category === 'Attorney')} placeholder="Search attorneys…" required data-id="element-162" />
            <LookupField label="Seller's Attorney" value={state.sellerAttorney} onChange={v => onChange('sellerAttorney', v)} options={contactDirectory.filter(c => c.category === 'Attorney')} placeholder="Search attorneys…" required data-id="element-163" />
          </div>
        </section>}

      {/* HOA (Property Type Specific) */}
      {requiresHOA && <section className="pt-8 border-t border-slate-100 dark:border-slate-700" data-id="element-164">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-4 uppercase tracking-wider" data-id="element-165">
            Homeowners Association
          </h3>
          <Alert variant="info" className="mb-6" data-id="element-166">
            HOA contact is required for {state.propertyType} properties.
          </Alert>
          <div className="max-w-md" data-id="element-167">
            <Input label="Condo/Co-op HOA Contact" value={state.hoaContact} onChange={e => onChange('hoaContact', e.target.value)} required data-id="element-168" />
          </div>
        </section>}
    </div>;
}